import React from "react";
import { Button, DatePicker, Space } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";
import { parseDateOnlyForPicker, toUTCMidnight } from "./dateUtils";

const { RangePicker } = DatePicker;

export const isDateRangeFilter = (value) =>
  value != null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  (value.from || value.to);

export const hasActiveDateFilter = (value) =>
  Boolean(value) &&
  (typeof value === "string" || isDateRangeFilter(value));

export const formatDateFilterForAPI = (value) => {
  if (!value) {
    return undefined;
  }

  if (typeof value === "string") {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return undefined;
    }
    const utcDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    return utcDate.toISOString();
  }

  if (isDateRangeFilter(value)) {
    const formatted = {};
    if (value.from) {
      formatted.from = toUTCMidnight(value.from);
    }
    if (value.to) {
      formatted.to = toUTCMidnight(value.to);
    }
    return Object.keys(formatted).length > 0 ? formatted : undefined;
  }

  return undefined;
};

export const applyDateFiltersToQueryParams = (filters, dateKeys) => {
  const result = { ...filters };
  dateKeys.forEach((key) => {
    if (result[key]) {
      result[key] = formatDateFilterForAPI(result[key]);
    }
  });
  return result;
};

const toRangePickerValue = (filterValue) => {
  if (!isDateRangeFilter(filterValue)) {
    return null;
  }

  const start = filterValue.from ? parseDateOnlyForPicker(filterValue.from) : null;
  const end = filterValue.to ? parseDateOnlyForPicker(filterValue.to) : null;

  if (!start && !end) {
    return null;
  }

  return [start, end];
};

export const createGetColumnDateRangeProps =
  (filters, setFilters) => (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <RangePicker
          value={
            selectedKeys[0]
              ? toRangePickerValue(selectedKeys[0])
              : toRangePickerValue(filters[dataIndex])
          }
          onChange={(dates) => {
            if (!dates) {
              setSelectedKeys([]);
              return;
            }

            const [start, end] = dates;
            setSelectedKeys([
              {
                ...(start ? { from: toUTCMidnight(start) } : {}),
                ...(end ? { to: toUTCMidnight(end) } : {}),
              },
            ]);
          }}
          format={DATE_PICKER_FORMAT}
          style={{ marginBottom: 8, display: "block" }}
          allowEmpty={[true, true]}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            style={{ width: 90 }}
            onClick={() => {
              const nextValue = selectedKeys[0] ?? filters[dataIndex];
              if (nextValue?.from || nextValue?.to) {
                setFilters((prev) => ({
                  ...prev,
                  [dataIndex]: nextValue,
                }));
              } else {
                setFilters((prev) => {
                  const updated = { ...prev };
                  delete updated[dataIndex];
                  return updated;
                });
              }
              confirm();
            }}
          >
            Filter
          </Button>
          <Button
            size="small"
            style={{ width: 90 }}
            onClick={() => {
              clearFilters?.();
              setSelectedKeys([]);
              setFilters((prev) => {
                const updated = { ...prev };
                delete updated[dataIndex];
                return updated;
              });
              confirm();
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <CalendarOutlined
        style={{ color: filtered ? "#1677ff" : undefined }}
        title={`Filter ${title}`}
      />
    ),
    filteredValue: hasActiveDateFilter(filters[dataIndex])
      ? [filters[dataIndex]]
      : null,
  });
